import { useMemo } from "react";
import { Box } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { colors } from "../ui/colors";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const GREEN = "#16a376";

const trendLineData = (values) => {
  const n = values.length;
  if (n < 2) return null;
  const xs = values.map((_, i) => i);
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = values.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i] - meanX) * (values[i] - meanY);
    den += (xs[i] - meanX) * (xs[i] - meanX);
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanY - slope * meanX;
  return xs.map((x) => +(intercept + slope * x).toFixed(2));
};

const BodyMeasurementChart = ({ data, unit = "" }) => {
  const points = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const labels = useMemo(() => points.map((p) => p.date), [points]);
  const values = useMemo(() => points.map((p) => p.value), [points]);
  const trend = useMemo(() => trendLineData(values), [values]);

  const chart = useMemo(() => {
    if (points.length === 0) return null;
    const datasets = [
      {
        label: "מדידה",
        data: values,
        borderColor: GREEN,
        backgroundColor: `${GREEN}22`,
        borderWidth: 2.5,
        pointRadius: 3.5,
        pointHoverRadius: 5,
        pointBackgroundColor: GREEN,
        pointBorderColor: "#fff",
        pointBorderWidth: 1.5,
        tension: 0.35,
        fill: true,
      },
    ];
    if (trend) {
      datasets.push({
        label: "מגמה",
        data: trend,
        borderColor: "rgba(100,100,100,0.4)",
        borderDash: [5, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
      });
    }
    return {
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 600 },
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#fff",
            titleColor: colors.textSecondary,
            bodyColor: colors.textPrimary,
            borderColor: "rgba(15,20,40,.1)",
            borderWidth: 1,
            padding: 8,
            cornerRadius: 8,
            displayColors: false,
            titleFont: { size: 10, weight: "600" },
            bodyFont: { size: 11, weight: "700" },
            callbacks: {
              title: (items) => {
                if (!items || items.length === 0) return "";
                const idx = items[0].dataIndex;
                return points[idx]?.fullDate || points[idx]?.date || "";
              },
              label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}${unit ? ` ${unit}` : ""}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              font: { size: 10 },
              color: colors.textSecondary,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 6,
            },
          },
          y: {
            grid: { color: "rgba(0,0,0,.04)" },
            border: { display: false },
            ticks: {
              font: { size: 10 },
              color: colors.textSecondary,
              padding: 8,
            },
          },
        },
      },
    };
  }, [points, values, trend, labels, unit]);

  if (!chart) {
    return (
      <Box
        sx={{
          background: "#fff",
          border: "1px dashed rgba(15,20,40,.12)",
          borderRadius: "10px",
          padding: "30px",
          textAlign: "center",
          color: colors.textSecondary,
          fontSize: 13,
        }}
      >
        אין נתונים להצגה
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid rgba(15,20,40,.08)",
        borderRadius: "10px",
        padding: "14px",
        boxShadow: "0 1px 3px rgba(15,20,40,.04)",
        position: "relative",
      }}
    >
      {unit && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 12,
            fontSize: 10,
            color: colors.textSecondary,
            fontWeight: 600,
          }}
        >
          {unit}
        </Box>
      )}
      <Box sx={{ height: 200, direction: "ltr" }}>
        <Line data={chart.data} options={chart.options} />
      </Box>
    </Box>
  );
};

export default BodyMeasurementChart;
